import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Pencil, Save, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, apiError, isAdmin, isLeader, isSuperAdmin } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const L = ({ children }) => (
  <Label className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500">{children}</Label>
);

const CATEGORIES = ["material", "spare_part", "other"];

export default function PriceList() {
  const { t } = useTranslation();
  const { user, company } = useAuth();
  const ro = !!company?.demo_readonly;
  const admin = isAdmin(user?.role) && !ro;
  const leader = isLeader(user?.role) && !ro;
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({ name: "", price: "", unit: "st", category: "material" });
  const [rates, setRates] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([api.get("/price-items"), api.get("/settings")]);
      setItems(p.data);
      setRates(s.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name.trim()) return;
    try {
      const payload = {
        name: form.name.trim(),
        price: Number(form.price) || 0,
        unit: form.unit || "st",
        category: form.category,
      };
      if (form.id) await api.put(`/price-items/${form.id}`, payload);
      else await api.post("/price-items", payload);
      toast.success(t("price.itemSaved"));
      setForm({ name: "", price: "", unit: "st", category: form.category });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(t("price.deleteConfirm", { name: item.name }))) return;
    try {
      await api.delete(`/price-items/${item.id}`);
      toast.success(t("price.itemRemoved"));
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveRates = async () => {
    try {
      const { data } = await api.put("/settings", {
        work_rate: Number(rates.work_rate) || 0,
        travel_rate: Number(rates.travel_rate) || 0,
        mil_rate: Number(rates.mil_rate) || 0,
        vat_percent: Number(rates.vat_percent) || 0,
        base_address: rates.base_address,
      });
      setRates(data);
      toast.success(t("price.ratesSaved"));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const filtered = filter === "all" ? items : items.filter((i) => i.category === filter);

  return (
    <div data-testid="price-list-page">
      <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">{t("price.title")}</h1>
      <p className="mb-4 text-sm text-slate-500">
        {t("price.subtitle")}
      </p>

      {isSuperAdmin(user?.role) && rates && (
        <div className="mb-5 rounded-lg border border-slate-200 bg-white p-4" data-testid="rates-panel">
          <h2 className="flex items-center gap-2 text-xl font-semibold text-slate-800">
            <Settings2 className="h-4 w-4 text-slate-400" /> {t("price.ratesTitle")}
          </h2>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              ["work_rate", t("price.workRate")],
              ["travel_rate", t("price.travelRate")],
              ["mil_rate", t("price.milRate")],
              ["vat_percent", t("price.vatPercent")],
            ].map(([key, label]) => (
              <div key={key}>
                <L>{label}</L>
                <Input
                  data-testid={`rate-${key}-input`}
                  type="number"
                  step="0.01"
                  value={rates[key] ?? ""}
                  onChange={(e) => setRates({ ...rates, [key]: e.target.value })}
                  className="mt-1 h-10"
                />
              </div>
            ))}
          </div>
          <div className="mt-3">
            <L>{t("price.baseAddress")}</L>
            <Input
              data-testid="rate-base-address-input"
              value={rates.base_address || ""}
              onChange={(e) => setRates({ ...rates, base_address: e.target.value })}
              className="mt-1 h-10"
            />
          </div>
          <Button
            data-testid="rates-save-button"
            onClick={saveRates}
            className="mt-3 h-10 bg-blue-600 text-white transition-colors duration-200 hover:bg-blue-700"
          >
            <Save className="mr-1.5 h-4 w-4" /> {t("price.saveRates")}
          </Button>
        </div>
      )}

      <Tabs value={filter} onValueChange={setFilter} className="mb-3">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="all" data-testid="price-tab-all">{t("common.all")} ({items.length})</TabsTrigger>
          {CATEGORIES.map((k) => (
            <TabsTrigger key={k} value={k} data-testid={`price-tab-${k}`}>
              {t(`price.${k}`)} ({items.filter((i) => i.category === k).length})
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {leader && (
      <div className="mb-4 grid grid-cols-12 gap-2 rounded-lg border border-slate-200 bg-white p-3" data-testid="price-item-form">
        <Input
          data-testid="price-name-input"
          placeholder={t("common.name")}
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="col-span-12 h-10 sm:col-span-4"
        />
        <Input
          data-testid="price-price-input"
          type="number"
          step="0.01"
          placeholder={t("common.price")}
          value={form.price}
          onChange={(e) => setForm({ ...form, price: e.target.value })}
          className="col-span-4 h-10 sm:col-span-2"
        />
        <Input
          data-testid="price-unit-input"
          placeholder={t("common.unit")}
          value={form.unit}
          onChange={(e) => setForm({ ...form, unit: e.target.value })}
          className="col-span-3 h-10 sm:col-span-2"
        />
        <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
          <SelectTrigger data-testid="price-category-select" className="col-span-5 h-10 sm:col-span-2">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white">
            {CATEGORIES.map((k) => (
              <SelectItem key={k} value={k} data-testid={`price-category-option-${k}`}>{t(`price.${k}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          data-testid="price-save-button"
          onClick={save}
          className="col-span-12 h-10 bg-blue-600 text-white transition-colors duration-200 hover:bg-blue-700 sm:col-span-2"
        >
          <Plus className="mr-1.5 h-4 w-4" /> {form.id ? t("common.update") : t("common.add")}
        </Button>
      </div>
      )}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        {filtered.length === 0 && (
          <p className="p-6 text-center text-sm text-slate-500" data-testid="price-list-empty">
            Inga artiklar i prislistan.
          </p>
        )}
        {filtered.map((i) => (
          <div
            key={i.id}
            data-testid={`price-row-${i.id}`}
            className="flex items-center gap-3 border-b border-slate-100 p-3 last:border-0 transition-colors duration-200 hover:bg-slate-50"
          >
            <span className="min-w-0 flex-1 text-sm font-semibold text-slate-900">{i.name}</span>
            <span className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[11px] font-medium text-slate-600">
              {t(`price.${i.category}`)}
            </span>
            <span className="text-sm font-medium text-slate-900">{i.price.toFixed(2)} kr/{i.unit}</span>
            {leader && (
              <>
                <button
                  data-testid={`price-edit-${i.id}`}
                  onClick={() => setForm({ ...i, price: String(i.price) })}
                  className="rounded p-2 text-slate-600 transition-colors duration-200 hover:bg-slate-100"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  data-testid={`price-delete-${i.id}`}
                  onClick={() => remove(i)}
                  className="rounded p-2 text-red-600 transition-colors duration-200 hover:bg-red-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
